import React, { useState } from 'react';
import { supabase } from '../supabaseClient';

const Login = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleGoogleLogin = async () => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          // Redirects back to Dashboard after Google auth
          redirectTo: `${window.location.origin}/dashboard`,
        },
      });
      if (error) throw error;
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden flex items-center justify-center p-4"
      style={{ background: 'linear-gradient(135deg, #0f0f11 0%, #1a1025 40%, #0f0f11 100%)' }}
    >

      {/* --- ANIMATED BACKGROUND ELEMENTS --- */}
      {/* Gradient orbs */}
      <div className="absolute top-[-20%] left-[-10%] w-[500px] h-[500px] rounded-full opacity-30 blur-3xl animate-pulse"
        style={{ background: 'radial-gradient(circle, #ee5b2b 0%, transparent 70%)' }}
      />
      <div className="absolute bottom-[-15%] right-[-10%] w-[400px] h-[400px] rounded-full opacity-20 blur-3xl animate-pulse"
        style={{ background: 'radial-gradient(circle, #7c3aed 0%, transparent 70%)', animationDelay: '1s' }}
      />
      <div className="absolute top-[50%] left-[60%] w-[300px] h-[300px] rounded-full opacity-15 blur-3xl animate-pulse"
        style={{ background: 'radial-gradient(circle, #f59e0b 0%, transparent 70%)', animationDelay: '2s' }}
      />

      {/* Floating food emojis */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <span className="absolute text-4xl opacity-10 animate-bounce" style={{ top: '10%', left: '8%', animationDuration: '3s' }}>🍕</span>
        <span className="absolute text-3xl opacity-10 animate-bounce" style={{ top: '20%', right: '12%', animationDuration: '4s', animationDelay: '0.5s' }}>🍔</span>
        <span className="absolute text-4xl opacity-10 animate-bounce" style={{ bottom: '25%', left: '15%', animationDuration: '3.5s', animationDelay: '1s' }}>🍜</span>
        <span className="absolute text-3xl opacity-10 animate-bounce" style={{ bottom: '15%', right: '8%', animationDuration: '4.5s', animationDelay: '1.5s' }}>🍰</span>
        <span className="absolute text-2xl opacity-10 animate-bounce" style={{ top: '45%', left: '5%', animationDuration: '5s', animationDelay: '0.7s' }}>☕</span>
        <span className="absolute text-3xl opacity-10 animate-bounce" style={{ top: '60%', right: '5%', animationDuration: '3.8s', animationDelay: '2s' }}>🥗</span>
      </div>

      {/* Grid pattern overlay */}
      <div className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)',
          backgroundSize: '60px 60px'
        }}
      />

      {/* --- MAIN CARD --- */}
      <div className="relative z-10 w-full max-w-sm animate-fade-in">

        {/* Glassmorphism Card */}
        <div className="backdrop-blur-xl bg-white/[0.07] border border-white/10 p-10 rounded-[2rem] shadow-2xl text-center relative overflow-hidden">

          {/* Card inner glow */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[200px] h-[1px] bg-gradient-to-r from-transparent via-white/30 to-transparent" />

          {/* Logo */}
          <div className="mb-8">
            <div className="w-20 h-20 mx-auto mb-5 bg-white/10 rounded-2xl flex items-center justify-center border border-white/10 shadow-lg backdrop-blur-sm">
              <img src="/favicon.png" alt="TableAR Logo" className="w-12 h-12" />
            </div>
            <h2 className="text-3xl font-black text-white mb-2 tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
              Welcome to <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-300">TableAR</span>
            </h2>
            <p className="text-gray-400 text-sm font-medium">Sign in to manage your 3D Menu</p>
          </div>

          {/* Error */}
          {error && (
            <div className="bg-red-500/10 text-red-400 p-3 rounded-xl text-xs mb-6 text-left border border-red-500/20 backdrop-blur-sm">
              {error}
            </div>
          )}

          {/* Google Button */}
          <button
            onClick={handleGoogleLogin}
            disabled={loading}
            className="w-full py-4 px-4 bg-white rounded-xl font-bold text-gray-800 hover:bg-gray-100 transition-all flex items-center justify-center gap-3 shadow-[0_0_30px_rgba(255,255,255,0.1)] active:scale-[0.98] hover:shadow-[0_0_40px_rgba(255,255,255,0.15)] disabled:opacity-50"
          >
            {loading ? (
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                <span>Connecting...</span>
              </div>
            ) : (
              <>
                <img
                  src="https://www.svgrepo.com/show/475656/google-color.svg"
                  alt="Google"
                  className="w-6 h-6"
                />
                <span>Continue with Google</span>
              </>
            )}
          </button>

          {/* Divider */}
          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-gray-500 text-xs font-medium uppercase tracking-widest">Secure Login</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          {/* Features mini-showcase */}
          <div className="flex justify-center gap-6 mb-6">
            <div className="text-center">
              <div className="text-2xl mb-1">📦</div>
              <p className="text-[10px] text-gray-500 font-medium">3D Models</p>
            </div>
            <div className="text-center">
              <div className="text-2xl mb-1">📊</div>
              <p className="text-[10px] text-gray-500 font-medium">Analytics</p>
            </div>
            <div className="text-center">
              <div className="text-2xl mb-1">🛒</div>
              <p className="text-[10px] text-gray-500 font-medium">Orders</p>
            </div>
          </div>

          <p className="text-[11px] text-gray-500">
            By continuing, you agree to our{' '}
            <a href="/terms" className="text-gray-400 hover:text-white transition-colors underline underline-offset-2">Terms</a>
            {' '}and{' '}
            <a href="/privacy" className="text-gray-400 hover:text-white transition-colors underline underline-offset-2">Privacy Policy</a>.
          </p>
        </div>

        {/* Powered by footer */}
        <p className="text-center text-gray-600 text-xs mt-6 font-medium">
          Powered by <span className="text-indigo-400 font-bold">TableAR</span> · India's AR Menu Platform
        </p>
      </div>
    </div>
  );
};

export default Login;